import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    LinearProgress,
    TablePagination,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import TuneIcon from '@material-ui/icons/Tune'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck'
import CloseIcon from '@material-ui/icons/Close'
import AutorenewIcon from '@material-ui/icons/Autorenew'

import { Container } from '../../../components'
import { ActionItemSuggestionCard } from './ActionItemSuggestionCard'
import { ActionItemFilterDrawer } from './ActionItemFilterDrawer'
import { ActionItemBulkActionDrawer } from './ActionItemBulkActionDrawer'
import {
    ActionItemSuggestion,
    ActionItemFilterState,
    SuggestionDecision,
    defaultFilterState,
} from '../../../hooks/useActionItemSuggestions/types'
import { typographyStyle } from '../../../theme/typographyStyle'
import { LoriText } from '../../../navigation/layouts/Lori/Icons/LoriText'
import {
    useActionItemSuggestions,
    useGenerateSuggestions,
    useCompany,
    useService,
    useFinderSelection,
    useRootInfrastructure,
    useUser,
} from '../../../hooks'

interface SuggestionState {
    selectedServiceId: number | null
    selectedVendorId: number | null
    myTeamMode: boolean
    selectedUserId: number | null
}

const buildDefaultSuggestionState = (
    suggestion: ActionItemSuggestion,
): SuggestionState => {
    const topService = [...suggestion.suggested_services].sort(
        (a, b) => b.confidence - a.confidence,
    )[0]
    const topVendor = topService
        ? [...topService.suggested_vendors].sort(
              (a, b) => b.confidence - a.confidence,
          )[0]
        : undefined
    return {
        selectedServiceId: topService?.service_id ?? null,
        selectedVendorId: topVendor?.vendor_id ?? null,
        myTeamMode: false,
        selectedUserId: null,
    }
}

interface InspectionActionItemsProps {
    inspectionId?: number
}

export const InspectionActionItems: React.FC<InspectionActionItemsProps> = ({
    inspectionId,
}) => {
    const theme = useTheme()

    const {
        suggestions,
        summary,
        bulkActions,
        matchingSuggestionIds,
        inspectionName,
        inspectionType,
        isLoading,
        page,
        setPage,
        pageSize,
        setPageSize,
        sortDirection,
        setSortDirection,
        filters,
        setFilters,
        acceptSuggestions,
        rejectSuggestions,
    } = useActionItemSuggestions(inspectionId)

    const {
        startGenerate,
        isGenerating,
        progress: generateProgress,
        resetProgress: resetGenerateProgress,
    } = useGenerateSuggestions(inspectionId)

    useEffect(() => {
        if (generateProgress?.state === 'FAILURE') {
            resetGenerateProgress()
        }
    }, [generateProgress, resetGenerateProgress])

    type ActiveDrawer = 'filter' | 'bulk' | null
    const [activeDrawer, setActiveDrawer] = useState<ActiveDrawer>(null)
    const toggleDrawer = (drawer: ActiveDrawer) =>
        setActiveDrawer((prev) => (prev === drawer ? null : drawer))

    const { tree } = useRootInfrastructure(true)
    const finderSelection = useFinderSelection('inspection')

    const { serviceList } = useService({ getServiceList: true })
    const { apartmentVendorList, getApartmentVendorList } = useCompany({})
    useEffect(() => {
        getApartmentVendorList()
    }, [])

    const { userList, actions } = useUser()
    useEffect(() => {
        actions.getUserList({ params: { my_team: true } })
    }, [])

    const isBulkMode = activeDrawer === 'bulk'
    const [bulkSelection, setBulkSelection] = useState<Set<number>>(new Set())

    const [suggestionStates, setSuggestionStates] = useState<
        Map<number, SuggestionState>
    >(new Map())
    const suggestionStatesRef = React.useRef(suggestionStates)
    suggestionStatesRef.current = suggestionStates

    useEffect(() => {
        if (!suggestions.length) return
        setSuggestionStates((prev) => {
            const next = new Map(prev)
            suggestions.forEach((s) => {
                if (!next.has(s.suggestion_id)) {
                    next.set(s.suggestion_id, buildDefaultSuggestionState(s))
                }
            })
            return next
        })
    }, [suggestions])

    const defaultState: SuggestionState = {
        selectedServiceId: null,
        selectedVendorId: null,
        myTeamMode: false,
        selectedUserId: null,
    }

    const getState = (suggestionId: number): SuggestionState => {
        return suggestionStatesRef.current.get(suggestionId) ?? defaultState
    }

    const updateState = (
        suggestionId: number,
        updates: Partial<SuggestionState>,
    ) => {
        setSuggestionStates((prev) => {
            const next = new Map(prev)
            const current = next.get(suggestionId) ?? defaultState
            next.set(suggestionId, { ...current, ...updates })
            return next
        })
    }

    useEffect(() => {
        if (isBulkMode) {
            setBulkSelection(new Set(matchingSuggestionIds))
        } else {
            setBulkSelection(new Set())
        }
    }, [isBulkMode])

    const toggleBulkSelect = useCallback((suggestionId: number) => {
        setBulkSelection((prev) => {
            const next = new Set(prev)
            if (next.has(suggestionId)) {
                next.delete(suggestionId)
            } else {
                next.add(suggestionId)
            }
            return next
        })
    }, [])

    const handleSelectAll = useCallback(() => {
        setBulkSelection(new Set(matchingSuggestionIds))
    }, [matchingSuggestionIds])

    const handleDeselectAll = useCallback(() => {
        setBulkSelection(new Set())
    }, [])

    const handleAccept = useCallback(
        (suggestionId: number) => {
            const state = getState(suggestionId)
            acceptSuggestions.mutate({
                suggestion_ids: [suggestionId],
                service_id: state.selectedServiceId,
                vendor_id: state.myTeamMode ? null : state.selectedVendorId,
                assigned_user_id: state.myTeamMode
                    ? state.selectedUserId
                    : null,
            })
        },
        [acceptSuggestions],
    )

    const handleReject = useCallback(
        (suggestionId: number) => {
            rejectSuggestions.mutate({ suggestion_ids: [suggestionId] })
        },
        [rejectSuggestions],
    )

    const handleBulkAccept = useCallback(
        (
            serviceId: number | null,
            vendorId: number | null,
            myTeamMode: boolean,
            userId: number | null,
        ) => {
            const ids = Array.from(bulkSelection)
            if (!ids.length) return
            acceptSuggestions.mutate({
                suggestion_ids: ids,
                service_id: serviceId,
                vendor_id: myTeamMode ? null : vendorId,
                assigned_user_id: myTeamMode ? userId : null,
            })
        },
        [bulkSelection, acceptSuggestions],
    )

    const handleBulkReject = useCallback(() => {
        const ids = Array.from(bulkSelection)
        if (!ids.length) return
        rejectSuggestions.mutate({ suggestion_ids: ids })
    }, [bulkSelection, rejectSuggestions])

    const totalPendingCount = summary?.pending_count ?? 0
    const totalAcceptedCount = summary?.accepted_count ?? 0
    const totalRejectedCount = summary?.rejected_count ?? 0
    const totalCount =
        totalPendingCount + totalAcceptedCount + totalRejectedCount

    const filteredPendingCount = summary?.filtered_pending_count ?? 0
    const filteredAcceptedCount = summary?.filtered_accepted_count ?? 0
    const filteredRejectedCount = summary?.filtered_rejected_count ?? 0
    const filteredCount =
        filteredPendingCount + filteredAcceptedCount + filteredRejectedCount

    const serviceMap = useMemo(() => {
        const map = new Map<number, string>()
        serviceList.forEach((svc) => map.set(svc.id, svc.name))
        return map
    }, [serviceList])

    const vendorMap = useMemo(() => {
        const map = new Map<number, string>()
        apartmentVendorList.forEach((av) =>
            map.set(av.vendor.id, av.vendor.name),
        )
        return map
    }, [apartmentVendorList])

    const selectedUnits = useMemo(() => {
        const units: { id: number; name: string }[] = []
        const sel = finderSelection.finderSelection
        if (sel?.unit) {
            Object.keys(sel.unit).forEach((key) => {
                if (key === 'length') return
                const u = sel.unit[Number(key)]
                if (u && typeof u !== 'number')
                    units.push({ id: u.id, name: u.name })
            })
        }
        return units
    }, [finderSelection.finderSelection])

    const selectedFolders = useMemo(() => {
        const folders: { id: number; name: string }[] = []
        const sel = finderSelection.finderSelection
        if (sel?.folder) {
            Object.keys(sel.folder).forEach((key) => {
                if (key === 'length') return
                const f = sel.folder[Number(key)]
                if (f && typeof f !== 'number')
                    folders.push({ id: f.id, name: f.name })
            })
        }
        return folders
    }, [finderSelection.finderSelection])

    useEffect(() => {
        const ids = selectedUnits.map((u) => u.id)
        setFilters((prev) => {
            const prevIds = prev.unitIds
            if (
                prevIds.length === ids.length &&
                prevIds.every((id, i) => id === ids[i])
            ) {
                return prev
            }
            return { ...prev, unitIds: ids }
        })
    }, [selectedUnits])

    const hasActiveFilters = useMemo(() => {
        return (
            filters.statuses.length > 0 ||
            filters.flagFilter !== 'no_filter' ||
            filters.confidenceOperator !==
                defaultFilterState.confidenceOperator ||
            filters.confidenceThreshold !==
                defaultFilterState.confidenceThreshold ||
            filters.services.length > 0 ||
            filters.vendors.length > 0 ||
            selectedUnits.length > 0 ||
            selectedFolders.length > 0
        )
    }, [filters, selectedUnits, selectedFolders])

    const clearAllFilters = () => {
        setFilters(defaultFilterState)
        selectedUnits.forEach((u) =>
            finderSelection.selectLocation({
                type: 'unit',
                location: { id: u.id, name: u.name } as any,
            }),
        )
        selectedFolders.forEach((f) =>
            finderSelection.selectLocation({
                type: 'folder',
                location: { id: f.id, name: f.name } as any,
            }),
        )
    }

    const wrappedData = useMemo(
        () => ({
            suggestions,
        }),
        [suggestions],
    )

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Container
                style={{
                    padding: theme.spacing(2),
                    alignItems: 'stretch',
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    flexShrink: 0,
                    gap: theme.spacing(2),
                }}
            >
                {/* Title Section */}
                <Container
                    style={{
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: theme.spacing(0.5),
                        flexShrink: 0,
                    }}
                >
                    <Container style={{ alignItems: 'center', gap: 8 }}>
                        <LoriText
                            width={64}
                            height={24}
                            color={theme.palette.primary.main}
                        />
                        <Button
                            variant="outlined"
                            disabled={isGenerating}
                            startIcon={
                                isGenerating ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <AutorenewIcon />
                                )
                            }
                            onClick={startGenerate}
                            style={{
                                textTransform: 'none',
                                minWidth: 150,
                                justifyContent: 'flex-start',
                            }}
                        >
                            {isGenerating
                                ? 'Generating...'
                                : 'Generate New Suggestions'}
                        </Button>
                    </Container>
                    {isGenerating && generateProgress && (
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing(1),
                                marginTop: 2,
                            }}
                        >
                            <LinearProgress
                                variant={
                                    generateProgress.state === 'PROGRESS' &&
                                    generateProgress.total
                                        ? 'determinate'
                                        : 'indeterminate'
                                }
                                value={
                                    generateProgress.state === 'PROGRESS' &&
                                    generateProgress.total
                                        ? ((generateProgress.current ?? 0) /
                                              generateProgress.total) *
                                          100
                                        : undefined
                                }
                                style={{
                                    flex: 1,
                                    minWidth: 120,
                                    height: 6,
                                    borderRadius: 3,
                                }}
                            />
                            <Typography
                                variant="caption"
                                style={{
                                    whiteSpace: 'nowrap',
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                {generateProgress.state === 'PROGRESS' &&
                                generateProgress.total
                                    ? `${generateProgress.current ?? 0} / ${
                                          generateProgress.total
                                      }`
                                    : 'Starting...'}
                            </Typography>
                            <Tooltip
                                title="Suggestions appear in batches as they're generated. After you pass 25 on this progress bar, they will start to display automatically.
                                You can begin working with these suggestions before all of the suggestions are generated. If you would like to wait, so that you can more
                                effectively use the bulk actions, you will receive an email when all suggestions are ready."
                                placement="right"
                            >
                                <InfoOutlinedIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.text.secondary,
                                        cursor: 'help',
                                    }}
                                />
                            </Tooltip>
                        </Box>
                    )}
                    <Container style={{ alignItems: 'baseline', gap: 6 }}>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle1),
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {inspectionName}
                        </span>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                            }}
                        >
                            &mdash;
                        </span>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {inspectionType}
                        </span>
                    </Container>
                </Container>

                <Divider orientation="vertical" flexItem />

                {/* Stat Tiles */}
                <Container
                    style={{
                        alignItems: 'center',
                        gap: theme.spacing(1.5),
                        flexShrink: 0,
                    }}
                >
                    {[
                        {
                            label: 'Total',
                            filteredValue: filteredCount,
                            totalValue: totalCount,
                            color: theme.palette.primary.main,
                        },
                        {
                            label: 'Accepted',
                            filteredValue: filteredAcceptedCount,
                            totalValue: totalAcceptedCount,
                            color: theme.palette.success.main,
                        },
                        {
                            label: 'Rejected',
                            filteredValue: filteredRejectedCount,
                            totalValue: totalRejectedCount,
                            color: theme.palette.error.main,
                        },
                        {
                            label: 'Pending',
                            filteredValue: filteredPendingCount,
                            totalValue: totalPendingCount,
                            color: theme.palette.text.secondary,
                        },
                    ].map((tile) => (
                        <Box
                            key={tile.label}
                            style={{
                                padding: theme.spacing(1),
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: theme.spacing(1),
                                backgroundColor: theme.palette.background.paper,
                                display: 'flex',
                                alignItems: 'stretch',
                                minWidth: 120,
                            }}
                        >
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: theme.spacing(0.5),
                                    width: '100%',
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: '0.85rem',
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {tile.label}
                                </span>
                                <span
                                    style={{
                                        width: '100%',
                                        textAlign: 'right',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '2rem',
                                            fontWeight: 700,
                                            color: tile.color,
                                        }}
                                    >
                                        {tile.filteredValue}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '1rem',
                                            fontWeight: 400,
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        {' / '}
                                        {tile.totalValue}
                                    </span>
                                </span>
                            </Box>
                        </Box>
                    ))}
                </Container>

                <Divider orientation="vertical" flexItem />

                {/* Drawer Action Buttons */}
                <Container
                    style={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        justifyContent: 'flex-end',
                        gap: theme.spacing(0.75),
                        flexShrink: 0,
                    }}
                >
                    {[
                        {
                            key: 'filter' as ActiveDrawer,
                            icon: <TuneIcon />,
                            label: 'Filters',
                        },
                        {
                            key: 'bulk' as ActiveDrawer,
                            icon: <PlaylistAddCheckIcon />,
                            label: 'Bulk Actions',
                        },
                    ].map((btn) => (
                        <Button
                            key={btn.key}
                            variant={
                                activeDrawer === btn.key
                                    ? 'contained'
                                    : 'outlined'
                            }
                            color={
                                activeDrawer === btn.key ? 'primary' : 'default'
                            }
                            startIcon={btn.icon}
                            onClick={() => toggleDrawer(btn.key)}
                            style={{
                                textTransform: 'none',
                                minWidth: 150,
                                justifyContent: 'flex-start',
                            }}
                        >
                            {btn.label}
                        </Button>
                    ))}
                </Container>
            </Container>

            {/* Filters Toolbar */}
            <Container
                style={{
                    padding: theme.spacing(0.75, 2),
                    backgroundColor: theme.palette.grey[50],
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    flexShrink: 0,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: theme.spacing(0.75),
                }}
            >
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                        sortDirection === 'desc' ? (
                            <ArrowDownwardIcon />
                        ) : (
                            <ArrowUpwardIcon />
                        )
                    }
                    onClick={() =>
                        setSortDirection(
                            sortDirection === 'desc' ? 'asc' : 'desc',
                        )
                    }
                    style={{
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                    }}
                >
                    {sortDirection === 'desc'
                        ? 'Conf. Low → High'
                        : 'Conf. High → Low'}
                </Button>

                {hasActiveFilters && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={clearAllFilters}
                        style={{
                            textTransform: 'none',
                            flexShrink: 0,
                        }}
                    >
                        Clear All
                    </Button>
                )}

                {filters.statuses.map((status: SuggestionDecision) => (
                    <Chip
                        key={`status-${status}`}
                        size="small"
                        label={
                            <span>
                                <span style={{ fontWeight: 600 }}>Status:</span>{' '}
                                {status.charAt(0).toUpperCase() +
                                    status.slice(1)}
                            </span>
                        }
                        onDelete={() =>
                            setFilters((prev: ActionItemFilterState) => ({
                                ...prev,
                                statuses: prev.statuses.filter(
                                    (s: SuggestionDecision) => s !== status,
                                ),
                            }))
                        }
                    />
                ))}

                {filters.flagFilter === 'has_flag' && (
                    <Chip
                        size="small"
                        label={
                            <span>
                                <span style={{ fontWeight: 600 }}>Flag:</span>{' '}
                                Flagged
                            </span>
                        }
                        onDelete={() =>
                            setFilters((prev: ActionItemFilterState) => ({
                                ...prev,
                                flagFilter: 'no_filter' as const,
                            }))
                        }
                    />
                )}
                {filters.flagFilter === 'no_flag' && (
                    <Chip
                        size="small"
                        label={
                            <span>
                                <span style={{ fontWeight: 600 }}>Flag:</span>{' '}
                                Not Flagged
                            </span>
                        }
                        onDelete={() =>
                            setFilters((prev: ActionItemFilterState) => ({
                                ...prev,
                                flagFilter: 'no_filter' as const,
                            }))
                        }
                    />
                )}

                {(filters.confidenceOperator !==
                    defaultFilterState.confidenceOperator ||
                    filters.confidenceThreshold !==
                        defaultFilterState.confidenceThreshold) && (
                    <Chip
                        size="small"
                        label={
                            <span>
                                <span style={{ fontWeight: 600 }}>
                                    Confidence:
                                </span>{' '}
                                {filters.confidenceOperator === 'gt'
                                    ? '>'
                                    : '<'}{' '}
                                {filters.confidenceThreshold}%
                            </span>
                        }
                        onDelete={() =>
                            setFilters((prev: ActionItemFilterState) => ({
                                ...prev,
                                confidenceOperator:
                                    defaultFilterState.confidenceOperator,
                                confidenceThreshold:
                                    defaultFilterState.confidenceThreshold,
                            }))
                        }
                    />
                )}

                {filters.services.map((svcId: number) => (
                    <Chip
                        key={`svc-${svcId}`}
                        size="small"
                        label={
                            <span>
                                <span style={{ fontWeight: 600 }}>
                                    Service:
                                </span>{' '}
                                {serviceMap.get(svcId) ?? svcId}
                            </span>
                        }
                        onDelete={() =>
                            setFilters((prev: ActionItemFilterState) => ({
                                ...prev,
                                services: prev.services.filter(
                                    (id: number) => id !== svcId,
                                ),
                            }))
                        }
                    />
                ))}

                {filters.vendors.map((vId: number) => (
                    <Chip
                        key={`vendor-${vId}`}
                        size="small"
                        label={
                            <span>
                                <span style={{ fontWeight: 600 }}>Vendor:</span>{' '}
                                {vendorMap.get(vId) ?? vId}
                            </span>
                        }
                        onDelete={() =>
                            setFilters((prev: ActionItemFilterState) => ({
                                ...prev,
                                vendors: prev.vendors.filter(
                                    (id: number) => id !== vId,
                                ),
                            }))
                        }
                    />
                ))}

                {selectedFolders.map((folder) => (
                    <Chip
                        key={`folder-${folder.id}`}
                        size="small"
                        label={
                            <span>
                                <span style={{ fontWeight: 600 }}>Bldg:</span>{' '}
                                {folder.name}
                            </span>
                        }
                        onDelete={() =>
                            finderSelection.selectLocation({
                                type: 'folder',
                                location: {
                                    id: folder.id,
                                    name: folder.name,
                                } as any,
                            })
                        }
                    />
                ))}

                {selectedUnits.map((unit) => (
                    <Chip
                        key={`unit-${unit.id}`}
                        size="small"
                        label={
                            <span>
                                <span style={{ fontWeight: 600 }}>Unit:</span>{' '}
                                {unit.name}
                            </span>
                        }
                        onDelete={() =>
                            finderSelection.selectLocation({
                                type: 'unit',
                                location: {
                                    id: unit.id,
                                    name: unit.name,
                                } as any,
                            })
                        }
                    />
                ))}
            </Container>

            {/* Card List */}
            <div
                style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: theme.spacing(2),
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(2),
                }}
            >
                {isLoading && (
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body1),
                            color: theme.palette.text.secondary,
                            textAlign: 'center',
                            padding: theme.spacing(4),
                        }}
                    >
                        Loading suggestions...
                    </span>
                )}
                {!isLoading && suggestions.length === 0 && (
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body1),
                            color: theme.palette.text.secondary,
                            textAlign: 'center',
                            padding: theme.spacing(4),
                        }}
                    >
                        No suggestions found
                    </span>
                )}
                {suggestions.map((suggestion) => {
                    const sid = suggestion.suggestion_id
                    const state = getState(sid)
                    return (
                        <ActionItemSuggestionCard
                            key={sid}
                            suggestion={suggestion}
                            decision={suggestion.status}
                            selectedServiceId={state.selectedServiceId}
                            selectedVendorId={state.selectedVendorId}
                            serviceList={serviceList}
                            apartmentVendorList={apartmentVendorList}
                            onSelectService={(serviceId) => {
                                const newService = suggestion.suggested_services.find(
                                    (s) => s.service_id === serviceId,
                                )
                                const topVendor = newService
                                    ? [...newService.suggested_vendors].sort(
                                          (a, b) => b.confidence - a.confidence,
                                      )[0]
                                    : undefined
                                updateState(sid, {
                                    selectedServiceId: serviceId,
                                    selectedVendorId:
                                        topVendor?.vendor_id ?? null,
                                })
                            }}
                            onSelectVendor={(vendorId) =>
                                updateState(sid, {
                                    selectedVendorId: vendorId,
                                })
                            }
                            myTeamMode={state.myTeamMode}
                            onToggleMyTeam={() =>
                                updateState(sid, {
                                    myTeamMode: !state.myTeamMode,
                                })
                            }
                            userList={userList}
                            selectedUserId={state.selectedUserId}
                            onSelectUser={(userId) =>
                                updateState(sid, { selectedUserId: userId })
                            }
                            onAccept={() => handleAccept(sid)}
                            onReject={() => handleReject(sid)}
                            isBulkMode={isBulkMode}
                            isSelected={bulkSelection.has(sid)}
                            onToggleSelect={() => toggleBulkSelect(sid)}
                        />
                    )
                })}
            </div>

            {/* Pagination Footer */}
            <div
                style={{
                    flexShrink: 0,
                    borderTop: `1px solid ${theme.palette.grey[200]}`,
                    display: 'flex',
                    justifyContent: 'flex-start',
                }}
            >
                <TablePagination
                    component="div"
                    count={filteredCount}
                    page={page - 1}
                    rowsPerPage={pageSize}
                    onChangePage={(_, newPage) => setPage(newPage + 1)}
                    onChangeRowsPerPage={(e) => {
                        setPageSize(parseInt(e.target.value, 10))
                        setPage(1)
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                />
            </div>

            {/* Drawers */}
            <ActionItemFilterDrawer
                open={activeDrawer === 'filter'}
                onClose={() => setActiveDrawer(null)}
                tree={tree.root}
                locationSelection={finderSelection.finderSelection}
                onSelectLocation={finderSelection.selectLocation}
                filter={filters}
                setFilter={setFilters}
                serviceList={serviceList}
                apartmentVendorList={apartmentVendorList}
            />
            <ActionItemBulkActionDrawer
                open={activeDrawer === 'bulk'}
                onClose={() => setActiveDrawer(null)}
                data={wrappedData}
                selectedCount={bulkSelection.size}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                userList={userList}
                onBulkAccept={handleBulkAccept}
                onBulkReject={handleBulkReject}
                minConfidence={bulkActions?.min_confidence ?? null}
                avgConfidence={bulkActions?.avg_confidence ?? null}
                maxConfidence={bulkActions?.max_confidence ?? null}
            />
        </Container>
    )
}
